<script setup lang="ts">
    import { ref } from 'vue';
    import { useCtUploadViewModel } from '@/views/case-settings/upload/ct/useCtUploadViewModel';
    import { numericColumnSort, type SortByItem } from '@/util/numericColumnSort';
    import AppModalTitle from '@/components/modal/AppModalTitle.vue';
    import AppModalContent from '@/components/modal/AppModalContent.vue';

    const upload = useCtUploadViewModel();

    const sortBy = ref<SortByItem[]>([]);

    const headers = [
        {title: 'Series #', key: 'seriesNumber', sort: numericColumnSort('seriesNumber', sortBy)},
        {title: 'Series Description', key: 'seriesDescription'},
        {title: 'Patient Name', key: 'patientName'},
        {title: 'Modality', key: 'modality'},
        {title: '# Images', key: 'fileCount', sort: numericColumnSort('fileCount', sortBy)},
        {
            title: 'Slice Thickness (mm)',
            key: 'sliceThickness',
            sort: numericColumnSort('sliceThickness', sortBy),
        },
        {title: 'Body Part', key: 'bodyPartExamined'},
        {title: 'Image Size', key: 'imageSize'},
    ];
</script>

<template>
    <app-modal
        :model-value="upload.isWaitingForSeriesSelection"
        persistent
        max-width="900"
        width="900"
    >
        <v-card>
            <app-modal-title @close="upload.cancel">
                {{ upload.hasSeries ? 'Select a DICOM series to upload' : 'No suitable series found' }}
            </app-modal-title>

            <v-divider />

            <app-modal-content>
                <template v-if="upload.hasSeries">
                    <v-card-text>
                        Multiple DICOM series were found in the uploaded folder. Select the
                        series you want to upload, then click Continue.
                    </v-card-text>

                    <v-data-table
                        v-model="upload.selected"
                        v-model:sort-by="sortBy"
                        :headers="headers"
                        :items="upload.seriesRows"
                        :items-per-page="-1"
                        item-value="seriesInstanceUid"
                        show-select
                        select-strategy="single"
                        density="compact"
                        class="series-selection-table"

                    >
                        <template #bottom></template>
                    </v-data-table>
                </template>

                <v-card-text v-else>
                    No suitable DICOM series could be found in the uploaded files. This usually
                    means the folder contained no CT images, only scout images, or only series
                    with fewer than 50 images. Please check the files and try again.
                </v-card-text>
            </app-modal-content>

            <app-modal-actions>
                <app-button-secondary width="100" class="mr-5" @click="upload.cancel">
                    {{ upload.hasSeries ? 'Cancel' : 'Close' }}
                </app-button-secondary>
                <app-button-primary
                    v-if="upload.hasSeries"
                    width="120"
                    :disabled="!upload.canContinue"
                    @click="upload.confirmSeriesSelection"
                >
                    Continue
                </app-button-primary>
            </app-modal-actions>
        </v-card>
    </app-modal>
</template>

<style scoped lang="scss">
    .series-selection-table {
        max-height: 480px;
        overflow-y: auto;
    }
</style>